import { useEffect, useRef, useState } from 'react';

/**
 * Single state machine for the hero's "show the system working" sequence:
 * the service-area map draws in, then 23 lead pings land on it one by one
 * while the report's "New leads" stat counts up in step, then the report's
 * own rows/next-week/Approved sequence takes over, then Keanu's assistant
 * callout slides in last. One shot, no loop, and every timer is skipped
 * entirely under `prefers-reduced-motion` -- that path renders the fully
 * settled state on the very first render instead.
 *
 * Timings are anchored to WeeklyReport's own internal timers (rows +150ms,
 * next-week +350ms, Approved +600ms after its `sequenceStart` flips) so
 * the callout genuinely waits for that sequence to finish, not a guess.
 */

const PING_COUNT = 23;
const MAP_DRAW_MS = 350;
const PING_SPAN_MS = 750;
const PING_INTERVAL_MS = PING_SPAN_MS / PING_COUNT;
const REPORT_START_AT_MS = MAP_DRAW_MS + PING_SPAN_MS;
// WeeklyReport settles its Approved chip 600ms after `sequenceStart`, plus
// a short buffer for the chip's own press/color transition to finish.
const REPORT_SETTLE_MS = 600 + 150;
const CALLOUT_BEAT_MS = 100;
export interface HeroSequence {
  heroRef: React.RefObject<HTMLElement>;
  mapDrawn: boolean;
  pingsLanded: number;
  reportStart: boolean;
  calloutIn: boolean;
}

export const useHeroSequence = (reduced: boolean): HeroSequence => {
  const heroRef = useRef<HTMLElement>(null);
  const [started, setStarted] = useState(false);
  const [mapDrawn, setMapDrawn] = useState(reduced);
  const [pingsLanded, setPingsLanded] = useState(reduced ? PING_COUNT : 0);
  const [reportStart, setReportStart] = useState(reduced);
  const [calloutIn, setCalloutIn] = useState(reduced);

  // Kick the whole sequence off once the hero enters view. Reduced-motion
  // visitors never need this -- every piece of state above already starts
  // in its settled shape.
  useEffect(() => {
    if (reduced || started || !heroRef.current) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setStarted(true);
      },
      { threshold: 0.3 }
    );
    io.observe(heroRef.current);
    return () => io.disconnect();
  }, [reduced, started]);

  // The one-shot lead-in: map draws, pings land one by one, the report's
  // own sequence takes over, then the callout appears last.
  useEffect(() => {
    if (reduced || !started) return undefined;
    const timers: number[] = [window.setTimeout(() => setMapDrawn(true), 20)];

    for (let i = 1; i <= PING_COUNT; i += 1) {
      timers.push(window.setTimeout(() => setPingsLanded(i), MAP_DRAW_MS + i * PING_INTERVAL_MS));
    }

    timers.push(window.setTimeout(() => setReportStart(true), REPORT_START_AT_MS));

    const calloutAt = REPORT_START_AT_MS + REPORT_SETTLE_MS + CALLOUT_BEAT_MS;
    timers.push(window.setTimeout(() => setCalloutIn(true), calloutAt));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [reduced, started]);

  return { heroRef, mapDrawn, pingsLanded, reportStart, calloutIn };
};
